import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type {
  FeedbackGroup,
  FeedbackGroupState,
  FeedbackType,
  GetFeedbackGroupResultType,
  ICustomerFeedbackGroupsContext,
} from '../../types/FeedbackGroup.types';
import { getFeedbackGroupResult } from '../../utils/customerFeedback';

export const CustomerFeedbackGroupsContext =
  createContext<ICustomerFeedbackGroupsContext>({
    feedbackGroups: [],
  });

/**
 * A provider to make order-level customer feedback group data available to all children components.
 */
type CustomerFeedbackGroupsProviderProps = {
  children: ReactNode;
  feedbackGroups: FeedbackGroup[];
};

export const CustomerFeedbackGroupsProvider = ({
  children,
  feedbackGroups,
}: CustomerFeedbackGroupsProviderProps) => {
  const feedbackGroupsDataContextValues = useMemo(
    () => ({
      feedbackGroups,
    }),
    [feedbackGroups]
  );

  return (
    <CustomerFeedbackGroupsContext.Provider
      value={feedbackGroupsDataContextValues}
    >
      {children}
    </CustomerFeedbackGroupsContext.Provider>
  );
};

/**
 *
 * @returns A feedback group result associated with the given line item.
 */
export type UseCustomerFeedbackGroupParams = {
  lineItemId: string;
  requestType: FeedbackType;
  groupState: FeedbackGroupState;
};

export const useCustomerFeedbackGroup = ({
  lineItemId,
  requestType,
  groupState,
}: UseCustomerFeedbackGroupParams): GetFeedbackGroupResultType => {
  const { feedbackGroups = [] } = useContext(CustomerFeedbackGroupsContext);

  return getFeedbackGroupResult({
    feedbackGroups,
    lineItemId,
    requestType,
    groupState,
  });
};

/**
 *
 * @returns A feedback group result associated with any given line items in the itemGroup.
 */
type UseCustomerFeedbackGroupForItemGroupParams = {
  lineItemIds: string[];
  requestType: FeedbackType;
  groupState: FeedbackGroupState;
};

export const useCustomerFeedbackGroupForItemGroup = ({
  lineItemIds,
  requestType,
  groupState,
}: UseCustomerFeedbackGroupForItemGroupParams): GetFeedbackGroupResultType => {
  const { feedbackGroups = [] } = useContext(CustomerFeedbackGroupsContext);

  for (let i = 0; i < lineItemIds.length; i += 1) {
    const lineItemId = lineItemIds[i]!;
    const result = getFeedbackGroupResult({
      feedbackGroups,
      lineItemId,
      requestType,
      groupState,
    });
    if (result.customerFeedbackGroupExists) return result;
  }

  return {
    customerFeedbackGroup: undefined,
    customerFeedbackGroupExists: false,
  };
};
